"use client";
import { useQuery } from "@tanstack/react-query";
import { seriesQueryKey, seriesService } from "@/services/serieService";
import { Series } from "@/types/Series";
import { PaginatedResponse } from "@/types/PaginatedResponse";
import { GenreFilters } from "@/modules/GenreFilters";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { CustomPagination } from "@/modules/CustomPagination";
import { useState, useEffect } from "react";
import { SeriesComponent } from "@/modules/Series";
import { useMobile } from "@/hooks/useMobile";

interface DiscoverySerieProps {
  page: number;
}

export function DiscoverySerie({ page }: DiscoverySerieProps) {
  const isMobile = useMobile();

  const take = isMobile ? 20 : 42;

  const sortBy = "popularity";
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);

  const {
    data: series,
    isLoading,
    refetch,
  } = useQuery<PaginatedResponse<Series>, Error>({
    queryKey: [seriesQueryKey, { take, page, sortBy, genres: selectedGenres }],
    queryFn: ({ queryKey }) => {
      const [, params] = queryKey as [
        string,
        { take: number; page: number; sortBy: string; genres: string[] },
      ];

      return seriesService.fetchSeries(
        params.take,
        params.page,
        params.sortBy,
        params.genres
      );
    },
  });

  useEffect(() => {
    refetch();
  }, [selectedGenres, refetch]);

  const totalPages = Math.ceil((series?.totalCount ?? 0) / take);

  return (
    <>
      <GenreFilters
        selectedGenres={selectedGenres}
        setSelectedGenres={setSelectedGenres}
        storageKey="series"
      />

      {series?.data?.length === 0 && (
        <div className="flex min-h-44 justify-center items-center">
          <h2 className="text-2xl font-semibold text-center">
            No series found for the selected filters.
          </h2>
        </div>
      )}

      {isLoading ? (
        <div className="flex min-h-44 justify-center items-center">
          <LoadingSpinner width={60} height={60} />
        </div>
      ) : (
        <>
          <div className="my-8" />
          <div
            className="justify-items-center grid gap-x-8 gap-y-8 md:gap-y-4"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))" }}>
            {series?.data.map(serie => <SeriesComponent key={serie.id} {...serie} />)}
          </div>
          <CustomPagination currentPage={page} totalPages={totalPages} />
        </>
      )}
    </>
  );
}
